import React, { useState, useEffect, useMemo, memo, useRef } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Form } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { OfferFormValues } from "../schemas/offerFormSchema";
import { CampLocation } from "@/types/campLocation";
import { Offer } from "@/types/offer";
import { SortableOption } from "@/types/fieldsOptions";
import { toast } from "sonner";

// Import refactored components
import { DeleteConfirmationDialog } from "./components/DeleteConfirmationDialog";
import { DialogActions } from "./components/DialogActions";
import { FormSections } from "./components/FormSections";
import { useOfferJson } from "./hooks/useOfferJson";
import { AdminOnlyInfoSection } from "./components/AdminOnlyInfoSection";
import EntityHistoryPanel from "@/components/common/EntityHistoryPanel";

interface OfferFormDialogUIProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSave: () => void;
  onDelete?: (id: string) => void;
  form: UseFormReturn<OfferFormValues>;
  initialOffer?: Offer | null;
  isAdmin: boolean;
  showDiff: () => void;
  isLoading: boolean;
  availabilityStatusOptions: SortableOption[];
  signupStatusOptions: SortableOption[];
  location: CampLocation;
  markFieldAsModified?: (fieldName: string) => void;
  isFieldModified?: (fieldName: string) => boolean;
  resetFieldToProgram?: (fieldName: string) => void;
  isCopyOperation?: boolean;
  showAddAnother?: boolean;
  addAnotherChecked?: boolean;
  onAddAnotherChange?: (checked: boolean) => void;
}

export const OfferFormDialogUI: React.FC<OfferFormDialogUIProps> = memo(({
  isOpen,
  onClose,
  title,
  onSave,
  onDelete,
  form,
  initialOffer,
  isAdmin,
  showDiff,
  isLoading,
  availabilityStatusOptions,
  signupStatusOptions,
  location,
  markFieldAsModified = () => {},
  isFieldModified = () => false,
  resetFieldToProgram = () => {},
  isCopyOperation = false,
  showAddAnother,
  addAnotherChecked,
  onAddAnotherChange
}) => {
  // Always initialize all state hooks, never conditionally
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bypassUnsavedCheck, setBypassUnsavedCheck] = useState(false);
  const [pendingAction, setPendingAction] = useState<'delete' | null>(null);
  
  // Use the extracted JSON hook
  const { copyToClipboard } = useOfferJson(initialOffer, form);
  
  // Track if form has been reset already to prevent infinite loops
  const formResetDoneRef = useRef(false);

  // Only log these values once on mount or when isOpen changes
  useEffect(() => {
    if (isOpen) {
      setBypassUnsavedCheck(false);
      setPendingAction(null);
      formResetDoneRef.current = false;
      
      // Important: Reset form's dirty state after initialization
      const timeoutId = setTimeout(() => {
        if (form && !formResetDoneRef.current) {
          form.reset(form.getValues(), { keepValues: true });
          formResetDoneRef.current = true;
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, form]); // Keep form as dependency but wrap internal logic in condition

  // Memoize the delete operation to prevent unnecessary re-renders
  const handleDelete = useMemo(() => async () => {
    // Early return if we don't have what we need
    if (!initialOffer || !initialOffer.id || !onDelete) {
      console.error("Cannot delete: Missing initialOffer or onDelete function");
      toast.error("Cannot delete: Missing required data");
      return;
    }

    try {
      setIsDeleting(true);
      setBypassUnsavedCheck(true);
      setPendingAction('delete');
      
      await onDelete(initialOffer.id);
      
      toast.success("Session deleted successfully");
      setIsDeleteDialogOpen(false);
      onClose();
    } catch (error) {
      console.error("Error in onDelete:", error);
      toast.error("Failed to delete session");
      setBypassUnsavedCheck(false);
      setPendingAction(null);
    } finally {
      setIsDeleting(false);
    }
  }, [initialOffer, onDelete, onClose]);

  const showDeleteButton = initialOffer && !isCopyOperation && onDelete;
  
  // Function to handle dialog close with unsaved changes check
  const handleDialogClose = () => {
    // If we're bypassing the check due to deletion, just close immediately
    if (bypassUnsavedCheck || pendingAction === 'delete') {
      onClose();
      return;
    }
    
    // Otherwise proceed with normal close behavior
    onClose();
  };

  // Memoize the isDirty value to prevent unnecessary re-renders
  const isDirty = useMemo(() => {
    return (bypassUnsavedCheck || pendingAction === 'delete') ? false : form.formState.isDirty;
  }, [bypassUnsavedCheck, pendingAction, form.formState.isDirty]);
  
  return (
    <>
      <BigFormModal 
        isOpen={isOpen} 
        onClose={handleDialogClose} 
        title={title} 
        onSave={onSave} 
        isSaving={form.formState.isSubmitting}
        isDirty={isDirty}
        pendingAction={pendingAction}
        showAddAnother={showAddAnother}
        addAnotherChecked={addAnotherChecked}
        onAddAnotherChange={onAddAnotherChange}
        extraActions={
          <DialogActions
            showDeleteButton={!!showDeleteButton}
            isAdmin={isAdmin}
            initialOffer={initialOffer}
            onCopyJson={copyToClipboard}
            onShowDiff={showDiff}
            onDelete={() => setIsDeleteDialogOpen(true)}
            isLoading={isLoading}
            isDeleting={isDeleting}
          />
        }
      >
        <Form {...form}>
          {/* Admin-only info section - replaces DocumentIdDisplay */}
          {initialOffer && (
            <AdminOnlyInfoSection 
              sessionId={initialOffer.id} 
              programId={location.id} 
            />
          )}
          
          {/* Form Sections - now passing isAdmin */}
          <FormSections
            form={form}
            location={location}
            markFieldAsModified={markFieldAsModified}
            isFieldModified={isFieldModified}
            resetFieldToProgram={resetFieldToProgram}
            availabilityStatusOptions={availabilityStatusOptions}
            signupStatusOptions={signupStatusOptions}
            isAdmin={isAdmin}
            hasInitialOffer={!!initialOffer}
            initialOffer={initialOffer}
            storedProgramUrl={initialOffer?.programUrl}
          />

          {/* History disclosure — per-field contribution log + Restore (KB-3319). Existing offers only. */}
          {initialOffer?.id && (
            <details className="mt-4 border-t pt-3" data-testid="offer-history-disclosure">
              <summary className="text-sm font-medium cursor-pointer">History</summary>
              <div className="mt-2">
                <EntityHistoryPanel kind="offer" id={initialOffer.id} audience={isAdmin ? "staff" : "director"} />
              </div>
            </details>
          )}
        </Form>
      </BigFormModal>
      
      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
});

OfferFormDialogUI.displayName = "OfferFormDialogUI";
